<template>
  <div class='relative w-full bg-[#1e3c53] rounded-xl shadow px-3 mb-5'
  @keydown.esc="showFilter = false">
    <WebSocketConnector @event-new-transit="newTransit"/>
    <div class="grid grid-cols-3 py-3">
      <div class="flex col-start-2 place-content-center gap-3">
        <LocationFilter
          uniqueComboName="liveLocationCombo"
          @update:filterByLocation="handleFilterByLocationChange"/>
        <SearchBar
          uniqueSearchBarName='liveTabSearchBar'
          :currentFetchParams='currentFetchParams'
          @update-fetch-params="searchBarTextChanged"/>
      </div>
    </div>
    <hr class="border-t border-sky-300 mb-2"/>
    <div class="pb-3">
      <LoadingOverlay v-if="isLoading" :show="isLoading" />
      <div v-else-if="transitStore.errors.fetchTransits"
      class="p-4 my-4 border rounded border-red-500 text-red-500">
      {{ transitStore.errors.fetchTransits }}
      </div>
      <div v-else-if="transitStore.transit?.transits?.length === 0"
        class="p-4 mt-4 border rounded bg-sky-100 border-sky-500 text-sky-800">
        <p class="text-2xl font-bold">{{ $t('transitHistoryTab.noTransitsToShow') }}</p>
      </div>
      <div v-else class="flex flex-wrap justify-center">
      <TransitCard
          v-for="transit in transitStore.transit.transits"
          :key="transit.id"
          :transit="transit"
          @transit-edited="(data) => this.setTransitPublishStatus(data.transitId, false)"
          @transit-published="(data) => this.setTransitPublishStatus(data.transitId, true)"
          :class="this.animateTransitId === transit.id ? 'animate-newtransit' : ''"/>
      </div>
    </div>
    <PopupImage
      :show="showPopup"
      :transitSections="this.transitSections"
      :isTrain="this.isTrainTransit"
      :transitId="this.popupTransitId"
      @close="showPopup = false"
      @transit-edited="setTransitAsEdited"/>
    <PopupVideo
      :show="showVideoPopup"
      :transitSections="this.transitSections"
      :isTrain="this.isTrainTransit"
      :transit="this.selectedVideoTransit"
      @close="showVideoPopup = false"/>
  </div>
</template>

<script>
import useUserManagementStore, { REFRESH_INTERVAL } from '@/stores/UserManagementStore';
import useTransitStore from '@/stores/TransitStore';
import PopupImage from '@/components/PopUpImage.vue';
import PopupVideo from '@/components/PopUpVideo.vue';
import TransitCard from '@/components/TransitCard.vue';
import SearchBar from '@/components/SearchBar.vue';
import WebSocketConnector from '@/components/WebSocketConnector.vue';
import LocationFilter from '@/components/LocationFilter.vue';
import LoadingOverlay from '@/components/LoadingOverlay.vue';

export default {
  name: 'TransitLiveTab',
  components: {
    LocationFilter,
    TransitCard,
    SearchBar,
    WebSocketConnector,
    PopupImage,
    PopupVideo,
    LoadingOverlay,
  },
  data() {
    return {
      showFilter: false,
      currentFetchParams: {
        searchValue: '',
        locations: [],
      },
      lastTransits: [],
      isLoading: true,
      animateTransitId: 0,
      showPopup: false,
      showVideoPopup: false,
      transitSections: [],
      isTrainTransit: false,
      selectedVideoTransit: {},
      liveModeAutoActivityIntervalId: 0,
      popupTransitId: 0,
    };
  },
  setup() {
    const transitStore = useTransitStore();
    return {
      transitStore,
    };
  },
  methods: {
    async refreshTransits() {
      try {
        await this.transitStore.fetchTransits(this.currentFetchParams);
        this.lastTransits = this.transitStore.transit.transits ?? [];
      } catch (err) {
        console.error(err);
      }
    },
    async searchBarTextChanged(params) {
      this.currentFetchParams.searchValue = params.searchValue;
      await this.refreshTransits();
    },
    async handleFilterByLocationChange(locations) {
      this.currentFetchParams.locations = locations;
      await this.refreshTransits();
    },
    async newTransit(transitId) {
      this.animateTransitId = 0;
      const newTransit = await this.transitStore.fetchTransit(transitId, this.currentFetchParams);
      if (newTransit) {
        if (this.lastTransits.length === 50) {
          this.lastTransits.pop();
        }
        this.lastTransits.unshift(newTransit);
        this.animateTransitId = transitId;
      }
    },
    setTransitPublishStatus(transitId, isPublished) {
      for (let i = 0; i < this.transitStore.transit.transits.length; i += 1) {
        const transit = this.transitStore.transit.transits[i];
        if (transit.id === transitId) {
          transit.is_published = isPublished;
          break;
        }
      }
    },
    setTransitAsEdited(transitId) {
      this.setTransitPublishStatus(transitId, false);
    },
    async openPopupEvent(transitObject) {
      this.popupTransitId = transitObject.transit.id;
      this.transitSections = transitObject.sections;
      this.isTrainTransit = transitObject.isTrain;
      this.showPopup = true;
    },
    async openVideoPopupEvent(transitObject) {
      this.showVideoPopup = true;
      this.selectedVideoTransit = transitObject.transit;
      this.transitSections = transitObject.sections;
      this.isTrainTransit = transitObject.isTrain;
    },
    restoreSessionFetchParams() {
      if (sessionStorage.liveLocationCombo) {
        this.currentFetchParams.locations = Object(JSON.parse(sessionStorage.liveLocationCombo));
      }
      if (sessionStorage.liveTabSearchBar) {
        this.currentFetchParams.searchValue = JSON.parse(sessionStorage.liveTabSearchBar);
      }
    },
  },
  async created() {
    try {
      this.isLoading = true;
      await this.transitStore.fetchLocation();
      this.emitter.emit('locations-updated', this.transitStore.locations);
      this.restoreSessionFetchParams();
      await this.refreshTransits();
    } catch (err) {
      console.error(err);
    } finally {
      this.isLoading = false;
    }
  },
  mounted() {
    this.emitter.on('click-on-image', this.openPopupEvent);
    this.emitter.on('click-on-video', this.openVideoPopupEvent);
    const liveModeAutoActivityInterval = REFRESH_INTERVAL / 2.0;
    this.liveModeAutoActivityIntervalId = setInterval(useUserManagementStore().setUserActivityOn, liveModeAutoActivityInterval);
  },
  unmounted() {
    this.emitter.off('click-on-image');
    this.emitter.off('click-on-video');
    clearInterval(this.liveModeAutoActivityIntervalId);
  },
};
</script>
